import { inject, Injectable } from '@angular/core';

import { CalculationService } from './calculation.service';
import { FilingFormState, FilingFormStateService } from './filing-form-state.service';
import { XbrlDataTransformerService } from './xbrl-data-transformer.service';
import { SHEET_KEYS } from '../../models/sheet-keys';
import { MbrsSheetData } from '../../../types/xbrl-preview';

/**
 * Maps a loaded MBRS conversion payload onto the 24 filing sheets' state.
 *
 * Extracted from `FilingFormContainerComponent` on 2026-08-24, where these 21
 * `populate*FromMbrsData` methods and their transform helpers made up roughly a
 * quarter of a 4,383-line component. They were already self-contained — they read
 * nothing off the component, only these three collaborators — so the move was
 * verbatim.
 *
 * Each `populate*FromMbrsData` takes the whole payload, picks out its own slice,
 * and writes it through `FilingFormStateService`. They are called once each, in
 * order, from the container's `fetchAllMbrsData`.
 *
 * **Add a new sheet's mapping here, not in the container** — the container's job is
 * orchestration (which sheets, in what order, and what to do when one is absent),
 * not per-sheet field mapping.
 */
@Injectable({ providedIn: 'root' })
export class FilingFormPopulationService {
  private stateService = inject(FilingFormStateService);
  private xbrlTransformer = inject(XbrlDataTransformerService);
  private calculationService = inject(CalculationService);

  /**
   * Populate FilingPrerequisite specifically from MBRS data (Role 0)
   */
  populateFilingPrerequisiteFromMbrsData(allData: MbrsSheetData): void {
    // Retrieving filingInfo data as filingPrerequisites data.
    const filingPrerequisitesData = allData['filing-info'];

    if (filingPrerequisitesData) {
      // Transform using service (Role 0 - passthrough)
      const transformedData = this.xbrlTransformer.transformRoleData(
        0,
        filingPrerequisitesData
      );

      // Store in FilingFormStateService
      this.stateService.loadFromAPI(transformedData, SHEET_KEYS.filingPrerequisites);
    }
  }

  /**
   * Populate FilingInfo specifically from MBRS data (Role 1)
   */
  populateFilingInfoFromMbrsData(allData: MbrsSheetData): void {
    const filingInfoData = allData['filing-info'];

    if (filingInfoData) {
      // Transform using service (Role 1 - passthrough)
      const transformedData = this.xbrlTransformer.transformRoleData(
        1,
        filingInfoData
      );

      // Store in FilingFormStateService as original state
      this.stateService.loadFromAPI(transformedData, SHEET_KEYS.filingInfo);
    }
  }

  /**
   * Populate ScopeOfFiling specifically from MBRS data (Role 2)
   */
  populateScopeOfFilingFromMbrsData(allData: MbrsSheetData): void {
    const scopeOfFilingData = allData['scope-of-filing'];

    if (scopeOfFilingData) {
      // Transform using service (Role 2 - business member)
      const transformedData = this.xbrlTransformer.transformRoleData(
        2,
        scopeOfFilingData
      );

      // Store in FilingFormStateService as original state
      // This will trigger the state listener in the scope-of-filing component
      this.stateService.loadFromAPI(transformedData, SHEET_KEYS.scopeOfFiling);
    } else {
      console.warn('⚠️ No scope-of-filing data found in backend response');
    }
  }

  /**
   * Populate DirectorBusinessReview specifically from MBRS data (Role 6)
   */
  populateDirectorBusinessReviewFromMbrsData(
    allData: MbrsSheetData
  ): void {
    const directorBusinessReviewData = allData['director-business-review'];

    if (directorBusinessReviewData) {
      // Transform using service (Role 6 - passthrough)
      const transformedData = this.xbrlTransformer.transformRoleData(
        6,
        directorBusinessReviewData
      );

      // Store in FilingFormStateService as original state
      // This will trigger the state listener in the director-business-review component
      this.stateService.loadFromAPI(transformedData, SHEET_KEYS.directorBusinessReview);
    } else {
      // No director-business-review data found in backend response
    }
  }

  /**
   * Populate DirectorsReport specifically from MBRS data (Role 4)
   */
  populateDirectorsReportFromMbrsData(allData: MbrsSheetData): void {
    const directorsReportData = allData['directors-report'];

    if (directorsReportData) {
      // Transform using service (Role 4 - passthrough)
      const transformedData = this.xbrlTransformer.transformRoleData(
        4,
        directorsReportData
      );

      // Store in FilingFormStateService as original state
      // This will trigger the state listener in the directors-report component
      this.stateService.loadFromAPI(transformedData, SHEET_KEYS.directorsReport);
    } else {
      // No directors-report data found in backend response
    }
  }

  /**
   * Populate StatementByDirectors specifically from MBRS data (Role 5)
   */
  populateStatementByDirectorsFromMbrsData(
    allData: MbrsSheetData
  ): void {
    const statementByDirectorsData = allData['statement-by-directors'];

    if (statementByDirectorsData) {
      // Transform using service (Role 5 - passthrough)
      const transformedData = this.xbrlTransformer.transformRoleData(
        5,
        statementByDirectorsData
      );

      // Store in FilingFormStateService as original state
      // This will trigger the state listener in the statement-by-directors component
      this.stateService.loadFromAPI(transformedData, SHEET_KEYS.statementByDirectors);
    } else {
      // No statement-by-directors data found in backend response
    }
  }

  /**
   * Populate StockExchange specifically from MBRS data (Role 3)
   */
  populateStockExchangeFromMbrsData(allData: MbrsSheetData): void {
    const stockExchangeData = allData['stock-exchange'];

    if (stockExchangeData) {
      // Transform using service (Role 3 - passthrough)
      const transformedData = this.xbrlTransformer.transformRoleData(
        3,
        stockExchangeData
      );

      // Store in FilingFormStateService as original state
      // This will trigger the state listener in the stock-exchange component
      this.stateService.loadFromAPI(transformedData, SHEET_KEYS.stockExchangeDisclosure);
    } else {
      // No stock-exchange data found in backend response
    }
  }

  /**
   * Populate AuditorsReport specifically from MBRS data (Role 7)
   */
  populateAuditorsReportFromMbrsData(allData: MbrsSheetData): void {
    const auditorsReportData = allData['auditors-report'];

    if (auditorsReportData) {
      // Transform using service (Role 7 - passthrough)
      const transformedData = this.xbrlTransformer.transformRoleData(
        7,
        auditorsReportData
      );

      // Store in FilingFormStateService as original state
      // This will trigger the state listener in the auditors-report component
      this.stateService.loadFromAPI(transformedData, SHEET_KEYS.auditorsReport);
    } else {
      // No auditors-report data found in backend response
    }
  }

  /**
   * Populate NotesCorporateInformation specifically from MBRS data (Role 20)
   */
  populateNotesCorporateInformationFromMbrsData(
    allData: MbrsSheetData
  ): void {
    const notesCorporateInformationData =
      allData['notes-corporate-information'];

    if (notesCorporateInformationData) {
      // Transform using service (Role 20 - passthrough)
      const transformedData = this.xbrlTransformer.transformRoleData(
        20,
        notesCorporateInformationData
      );

      // Store in FilingFormStateService as original state
      // This will trigger the state listener in the notes-corporate-information component
      this.stateService.loadFromAPI(
        transformedData,
        SHEET_KEYS.notesCorporateInformation
      );
    } else {
      // No notes-corporate-information data found in backend response
    }
  }

  /**
   * Populate NotesAccountingPolicies specifically from MBRS data (Role 21)
   */
  populateNotesAccountingPoliciesFromMbrsData(
    allData: MbrsSheetData
  ): void {
    const notesAccountingPoliciesData = allData['notes-accounting-policies'];

    if (notesAccountingPoliciesData) {
      // Transform using service (Role 21 - passthrough)
      const transformedData = this.xbrlTransformer.transformRoleData(
        21,
        notesAccountingPoliciesData
      );

      // Store in FilingFormStateService as original state
      // This will trigger the state listener in the notes-accounting-policies component
      this.stateService.loadFromAPI(transformedData, SHEET_KEYS.notesAccountingPolicies);
    } else {
      // No notes-accounting-policies data found in backend response
    }
  }

  /**
   * Populate NotesListOfNotes specifically from MBRS data (Role 22)
   */
  populateNotesListOfNotesFromMbrsData(allData: MbrsSheetData): void {
    const notesListOfNotesData = allData['notes-list-of-notes'];

    if (notesListOfNotesData) {
      // Transform using service (Role 22 - passthrough)
      const transformedData = this.xbrlTransformer.transformRoleData(
        22,
        notesListOfNotesData
      );

      // Store in FilingFormStateService as original state
      // This will trigger the state listener in the notes-list-of-notes component
      this.stateService.loadFromAPI(transformedData, SHEET_KEYS.notesListOfNotes);
    } else {
      // No notes-list-of-notes data found in backend response
    }
  }

  /**
   * Populate NotesIssuedCapital specifically from MBRS data (Role 23)
   */
  populateNotesIssuedCapitalFromMbrsData(allData: MbrsSheetData): void {
    const notesIssuedCapitalData = allData['notes-issued-capital'];

    if (notesIssuedCapitalData) {
      // Transform using service (Role 23 - double bracket)
      const transformedData = this.xbrlTransformer.transformRoleData(
        23,
        notesIssuedCapitalData
      );

      // Store in FilingFormStateService as original state
      // This will trigger the state listener in the notes-issued-capital component
      this.stateService.loadFromAPI(transformedData, SHEET_KEYS.notesIssuedCapital);
    } else {
      // No notes-issued-capital data found in backend response
    }
  }

  /**
   * Populate NotesRelatedPartyTransactions specifically from MBRS data (Role 24)
   */
  populateNotesRelatedPartyTransactionsFromMbrsData(
    allData: MbrsSheetData
  ): void {
    const notesRelatedPartyTransactionsData =
      allData['notes-related-party-transactions'];

    if (notesRelatedPartyTransactionsData) {
      // Transform using service (Role 24 - double bracket)
      const transformedData = this.xbrlTransformer.transformRoleData(
        24,
        notesRelatedPartyTransactionsData
      );

      // Store in FilingFormStateService as original state
      // This will trigger the state listener in the notes-related-party-transactions component
      this.stateService.loadFromAPI(
        transformedData,
        SHEET_KEYS.notesRelatedPartyTransactions
      );
    } else {
      // No notes-related-party-transactions data found in backend response
    }
  }

  /**
   * Populate StatementOfFinancialPosition specifically from MBRS data (Role 8)
   */
  public populateStatementOfFinancialPositionFromMbrsData(
    allData: MbrsSheetData
  ): void {
    const statementOfFinancialPosition =
      allData['statement-of-financial-position'];

    if (statementOfFinancialPosition) {
      // Transform using service (Role 8 - dimensional)
      const transformedData = this.xbrlTransformer.transformRoleData(
        8,
        statementOfFinancialPosition
      );

      // Detect XBRL prefix from the data (ifrs-full for MFRS, ifrs-smes for MPERS)
      const xbrlPrefix = this.detectXbrlPrefix(statementOfFinancialPosition);

      // Calculate Role8 totals using the CalculationService
      // This ensures calculated totals are stored in original state,
      // preventing false positives in hasAnySheetModified()
      const calculatedTotals = this.calculationService.calculateTotals_210000(
        transformedData,
        xbrlPrefix
      );

      // Register calculated keys so isSheetModified() skips them
      this.stateService.registerCalculatedKeys(
        SHEET_KEYS.statementOfFinancialPosition,
        Object.keys(calculatedTotals)
      );

      // Merge calculated totals with transformed data
      const completeData = { ...transformedData, ...calculatedTotals };

      // Store complete data (API data + calculated totals) in BOTH original and modified states
      this.stateService.loadFromAPI(
        completeData,
        SHEET_KEYS.statementOfFinancialPosition
      );
    }
  }

  /**
   * Populate SubClassification specifically from MBRS data (Role 9)
   */
  public populateSubClassificationFromMbrsData(allData: MbrsSheetData): void {
    const subClassification = allData['sub-classification'];

    if (subClassification) {
      // Transform using service (Role 9 - dimensional)
      const transformedData = this.xbrlTransformer.transformRoleData(
        9,
        subClassification
      );

      // Detect XBRL prefix from the data (ifrs-full for MFRS, ifrs-smes for MPERS)
      const xbrlPrefix = this.detectXbrlPrefix(subClassification);

      // Calculate Role9 totals using the CalculationService
      // This ensures totals are calculated immediately when data loads from API
      // so that Role8 can display them without needing to open Role9 first
      const calculatedTotals = this.calculationService.calculateTotals_210100(
        transformedData,
        xbrlPrefix
      );

      // Register calculated keys so isSheetModified() skips them
      this.stateService.registerCalculatedKeys(
        SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity,
        Object.keys(calculatedTotals)
      );

      // Merge calculated totals with transformed data
      const completeData = { ...transformedData, ...calculatedTotals };

      // Store complete data (API data + calculated totals) in BOTH original and modified states
      // This prevents false positives in hasAnySheetModified() where calculated totals
      // would differ from stored API values, causing the unsaved changes guard to trigger
      this.stateService.loadFromAPI(
        completeData,
        SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity
      );
    }
  }

  /**
   * Detect XBRL prefix (ifrs-full for MFRS, ifrs-smes for MPERS) from qnames
   */
  private detectXbrlPrefix(
    data: Record<string, any>
  ): 'ifrs-full' | 'ifrs-smes' {
    // Check if any qname uses ifrs-smes (MPERS)
    const hasMpers = Object.keys(data).some(
      (qname) => qname.startsWith('ifrs-smes:') || qname.includes('mpers')
    );

    if (hasMpers) {
      return 'ifrs-smes';
    }

    // Default to MFRS (ifrs-full)
    return 'ifrs-full';
  }

  /**
   * Populate StatementOfProfitOrLoss from MBRS data (Role 12)
   */
  public populateStatementOfProfitOrLossFromMbrsData(
    allData: MbrsSheetData
  ): void {
    const statementOfProfitOrLoss = allData['statement-of-profit-or-loss'];

    if (statementOfProfitOrLoss) {
      // Transform using service (Role 12 - simple format)
      const transformedData = this.xbrlTransformer.transformRoleData(
        12,
        statementOfProfitOrLoss
      );

      // Detect XBRL prefix from the data (ifrs-full for MFRS, ifrs-smes for MPERS)
      const xbrlPrefix = this.detectXbrlPrefix(statementOfProfitOrLoss);

      // Calculate Role12 totals using the CalculationService
      const calculatedTotals = this.calculationService.calculateTotals_310000(
        transformedData,
        xbrlPrefix
      );

      // Register calculated keys so isSheetModified() skips them
      this.stateService.registerCalculatedKeys(
        SHEET_KEYS.statementOfProfitOrLoss,
        Object.keys(calculatedTotals)
      );

      // Merge calculated totals with transformed data
      const completeData = { ...transformedData, ...calculatedTotals };

      // Store complete data (API data + calculated totals) in BOTH original and modified states
      this.stateService.loadFromAPI(completeData, SHEET_KEYS.statementOfProfitOrLoss);
    }
  }

  /**
   * Populate StatementOfProfitOrLossAnalysisFunction from MBRS data (Role 13)
   */
  public populateStatementOfProfitOrLossAnalysisFunctionFromMbrsData(
    allData: MbrsSheetData
  ): void {
    const analysisOfProfitOrLoss = allData['analysis-of-profit-or-loss'];

    if (analysisOfProfitOrLoss) {
      // Transform using service (Role 13 - simple format)
      const transformedData = this.xbrlTransformer.transformRoleData(
        13,
        analysisOfProfitOrLoss
      );

      // Detect XBRL prefix from the data (ifrs-full for MFRS, ifrs-smes for MPERS)
      const xbrlPrefix = this.detectXbrlPrefix(analysisOfProfitOrLoss);

      // Calculate Role13 totals using the CalculationService
      const calculatedTotals = this.calculationService.calculateTotals_310100(
        transformedData,
        xbrlPrefix
      );

      // Register calculated keys so isSheetModified() skips them
      this.stateService.registerCalculatedKeys(
        SHEET_KEYS.analysisOfProfitOrLoss,
        Object.keys(calculatedTotals)
      );

      // Merge calculated totals with transformed data
      const completeData = { ...transformedData, ...calculatedTotals };

      // Store complete data (API data + calculated totals) in BOTH original and modified states
      this.stateService.loadFromAPI(completeData, SHEET_KEYS.analysisOfProfitOrLoss);
    }
  }

  /**
   * Populate StatementOfComprehensiveIncome from MBRS data (Role 15)
   */
  public populateStatementOfComprehensiveIncomeFromMbrsData(
    allData: MbrsSheetData
  ): void {
    const comprehensiveIncome = allData['statement-of-comprehensive-income'];

    if (comprehensiveIncome) {
      // Transform using service (Role 15 - simple format)
      const transformedData = this.xbrlTransformer.transformRoleData(
        15,
        comprehensiveIncome
      );

      // Detect XBRL prefix from the data (ifrs-full for MFRS, ifrs-smes for MPERS)
      const xbrlPrefix = this.detectXbrlPrefix(comprehensiveIncome);

      // Calculate Role15 totals using the CalculationService
      const calculatedTotals = this.calculationService.calculateTotals_410000(
        transformedData,
        xbrlPrefix
      );

      // Register calculated keys so isSheetModified() skips them
      this.stateService.registerCalculatedKeys(
        SHEET_KEYS.statementOfComprehensiveIncome,
        Object.keys(calculatedTotals)
      );

      // Merge calculated totals with transformed data
      const completeData = { ...transformedData, ...calculatedTotals };

      // Store complete data (API data + calculated totals) in BOTH original and modified states
      this.stateService.loadFromAPI(
        completeData,
        SHEET_KEYS.statementOfComprehensiveIncome
      );
    }
  }

  /**
   * Populate StatementOfCashFlowsIndirect from MBRS data (Role 18)
   */
  public populateStatementOfCashFlowsIndirectFromMbrsData(
    allData: MbrsSheetData
  ): void {
    const socfIndirect = allData['statement-of-cash-flows-indirect'];

    if (socfIndirect) {
      // Transform using service (Role 18 - simple format)
      const transformedData = this.xbrlTransformer.transformRoleData(
        18,
        socfIndirect
      );

      // Detect XBRL prefix from the data (ifrs-full for MFRS, ifrs-smes for MPERS)
      const xbrlPrefix = this.detectXbrlPrefix(socfIndirect);

      // Calculate Role18 totals using the CalculationService
      const calculatedTotals = this.calculationService.calculateTotals_520000(
        transformedData,
        xbrlPrefix
      );

      // Register calculated keys so isSheetModified() skips them
      this.stateService.registerCalculatedKeys(
        SHEET_KEYS.statementOfCashFlowsIndirectMethod,
        Object.keys(calculatedTotals)
      );

      // Merge calculated totals with transformed data
      const completeData = { ...transformedData, ...calculatedTotals };

      // Store complete data (API data + calculated totals) in BOTH original and modified states
      this.stateService.loadFromAPI(
        completeData,
        SHEET_KEYS.statementOfCashFlowsIndirectMethod
      );
    }
  }

  /**
   * Populate StatementOfChangesInEquity from MBRS data (Role 19)
   * Transforms ALL concepts from API dimensional format to underscore-separated format
   *
   * Processes ALL tags/concepts in the API response, including but not limited to:
   * - ifrs-full:EquityAtBeginningOfPeriod
   * - ifrs-full:ChangesInEquity (with various member dimensions)
   * - ifrs-full:ComprehensiveIncome
   * - ifrs-full:EquityAtEndOfPeriod
   * - And any other concepts returned by the API
   *
   * API Format Examples:
   * - "ifrs-full:ChangesInEquity:IssuedCapitalMember" -> { sepCur, sepPrev, conCur, conPrev }
   * - "ifrs-full:EquityAtBeginningOfPeriod:EquityMember" -> { sepCur, sepPrev, conCur, conPrev }
   * - "ifrs-full:ChangesInEquity" -> { sepCur, sepPrev, conCur, conPrev } (no member)
   *
   * Form Format Examples:
   * - "ifrs-full:ChangesInEquity_ifrs-full:IssuedCapitalMember_Consolidated_Current"
   * - "ifrs-full:EquityAtBeginningOfPeriod_ifrs-full:EquityMember_Consolidated_Current"
   * - "ifrs-full:ChangesInEquity_ifrs-full:EquityMember_Consolidated_Current" (default member)
   */
  public populateStatementOfChangesInEquityFromMbrsData(
    allData: MbrsSheetData
  ): void {
    const soce = allData['statement-of-changes-in-equity'];

    if (soce) {
      // Transform using service (Role 19 - underscore format)
      const transformedData = this.xbrlTransformer.transformRoleData(19, soce);

      // Store in FilingFormStateService as original state - this contains ALL transformed concepts
      this.stateService.loadFromAPI(
        transformedData,
        SHEET_KEYS.statementOfChangesInEquity
      );
    } else {
      // No statement-of-changes-in-equity data found in backend response
    }
  }

  /**
   * Transform + store the N8N snapshot per monetary sheet, mirroring the populate
   * sequence so the stored snapshot is control-name-keyed (matching the
   * validateAgainstApiData reads in each sheet). Only the sheets that compute the
   * Validated* extracted-sum warning need it.
   */
  populateSnapshotFromMbrsData(snapshot: MbrsSheetData): void {
    // [slug, roleNumber, stateKey] — mirrors each populate…FromMbrsData method.
    // Every monetary sheet whose validateAgainstApiData reads the N8N snapshot
    // (mappingStatus.getSnapshotValues) for its "Validated*" extracted-value mismatch
    // must have its snapshot stored here.
    const sheets: Array<[string, number, keyof FilingFormState]> = [
      ['statement-of-financial-position', 8, SHEET_KEYS.statementOfFinancialPosition],
      ['sub-classification', 9, SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity],
      ['statement-of-profit-or-loss', 12, SHEET_KEYS.statementOfProfitOrLoss],
      ['analysis-of-profit-or-loss', 13, SHEET_KEYS.analysisOfProfitOrLoss],
      ['statement-of-comprehensive-income', 15, SHEET_KEYS.statementOfComprehensiveIncome],
      ['statement-of-cash-flows-indirect', 18, SHEET_KEYS.statementOfCashFlowsIndirectMethod],
      ['statement-of-changes-in-equity', 19, SHEET_KEYS.statementOfChangesInEquity],

      // --- Dormant pending BE snapshot support (see audit §7) ---
      // These sheets compute the extracted-sum warning but their N8N snapshot is not
      // yet returned by retrieve-mbrs-data. The slugs below are PROVISIONAL — confirm
      // against the backend snapshot payload + SHEET_SLUG_MAP when BE adds support.
      // Until then `snapshot[slug]` is undefined and the loop no-ops (if (!raw) continue).
      ['statement-of-financial-position-liquidity', 10, SHEET_KEYS.statementOfFinancialPositionLiquidity],
      ['sub-classification-liquidity', 11, SHEET_KEYS.subClassificationLiquidity],
      ['statement-of-profit-or-loss-by-nature', 14, SHEET_KEYS.statementOfProfitOrLossByNature],
      ['analysis-of-profit-or-loss-by-nature', 15, SHEET_KEYS.analysisOfProfitOrLossByNature],
      ['statement-of-comprehensive-income-before-tax', 16, SHEET_KEYS.statementOfComprehensiveIncomeBeforeTax],
      ['statement-of-cash-flows-direct', 17, SHEET_KEYS.statementOfCashFlowsDirectMethod],
    ];
    for (const [slug, roleN, stateKey] of sheets) {
      const raw = snapshot[slug];
      if (!raw) continue;
      const transformed = this.xbrlTransformer.transformRoleData(roleN, raw);
      this.stateService.setSnapshotSheetData(stateKey, transformed);
    }
  }
}
